import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SUMMARY_ENTITY_PRODUCTION } from 'constants/uiConstants';
import { useElementWidth } from 'hooks/common/useElementWidth';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput, IInputZone } from 'interfaces/IInput';
import { IInputTask } from 'interfaces/IInputTask';
import { PartialUserProfile } from 'interfaces/IUserProfile';

import {
  setTasksGridOpen,
  setTasksStepCategoryKey,
} from '../../../../redux/slices/InputSlice';
import { RootState } from '../../../../redux/Store';

import InputTasksHeader from './InputTasksHeader';
import OrgDefaultWaterRateField from './OrgDefaultWaterRateField';
import { ALL_STEP_CATEGORY_KEY } from './stepCategoryFilters';
import { ProductionCropContext } from './stepFactory';
import TasksTableGrid from './TasksTableGrid';
import { useTasksViewPreferences } from './tasksViewPreferences';
import { useRememberedExpandedTasks } from './useRememberedExpandedTasks';
import { useRememberedTaskCategory } from './useRememberedTaskCategory';

interface InputTasksProps {
  selectedInput: IInput;
  draftTasks: IInputTask[];
  // The plan's area and unit including unsaved edits, for new tasks.
  draftZone?: IInputZone | null;
  gUnits: GUnit[];
  productionCropContext: ProductionCropContext;
  userProfile?: PartialUserProfile;
  addDraftTask: (task: IInputTask) => void;
  updateDraftTask: (task: IInputTask) => void;
  removeDraftTask: (taskId: string) => void;
  schedulePendingTaskSave: (task: IInputTask) => void;
  schedulePendingNewTaskSave: (task: IInputTask) => void;
  schedulePendingTaskDelete: (taskId: string) => void;
}

const InputTasks: React.FC<InputTasksProps> = ({
  selectedInput,
  draftTasks,
  draftZone,
  gUnits,
  productionCropContext,
  userProfile,
  addDraftTask,
  updateDraftTask,
  removeDraftTask,
  schedulePendingTaskSave,
  schedulePendingNewTaskSave,
  schedulePendingTaskDelete,
}) => {
  // Kept in Redux so it's remembered when switching plans and Live totals
  // can follow it (OP-2535).
  const dispatch = useDispatch();
  const activeStepCategoryKey =
    useSelector(
      (state: RootState) => state.inputReducer.tasksStepCategoryKey
    ) ?? ALL_STEP_CATEGORY_KEY;
  const setActiveStepCategoryKey = useCallback(
    (categoryKey: string) =>
      dispatch(
        setTasksStepCategoryKey(
          categoryKey === ALL_STEP_CATEGORY_KEY ? null : categoryKey
        )
      ),
    [dispatch]
  );
  // Kept when moving between plans in the tree (OP-2556).
  const [activeTaskCategory, setActiveTaskCategory] =
    useRememberedTaskCategory();
  const { preferences, updatePreferences, resetViewOptions } =
    useTasksViewPreferences();
  const [expandedTaskKeys, setExpandedTaskKeys] = useRememberedExpandedTasks(
    selectedInput.id,
    draftTasks
  );

  // The new layout's collapsed tasks (OP-2556): products folded to one line.
  // Its own state, as the new layout starts with every task open.
  const [collapsedSheetTaskIds, setCollapsedSheetTaskIds] = useState<string[]>(
    []
  );

  useEffect(() => {
    setCollapsedSheetTaskIds([]);
  }, [selectedInput.id]);

  // Live totals follow the filter only while the grid is on screen, so they
  // never stay filtered on another tab or lens.
  useEffect(() => {
    dispatch(setTasksGridOpen(true));
    return () => {
      dispatch(setTasksGridOpen(false));
    };
  }, [dispatch]);

  const handleExpandAll = () => {
    if (preferences.sheetLayout) {
      setCollapsedSheetTaskIds([]);
      return;
    }
    setExpandedTaskKeys(draftTasks.map((task) => task.taskId));
  };

  const handleCollapseAll = () => {
    if (preferences.sheetLayout) {
      setCollapsedSheetTaskIds(draftTasks.map((task) => task.taskId));
      return;
    }
    setExpandedTaskKeys([]);
  };

  // The new layout's width cap, shared by the toolbar above the grid so its
  // right-hand controls line up with the grid's edge on wide screens.
  const [sheetMaxWidthClass, setSheetMaxWidthClass] = useState<string>();
  const { ref: areaRef, width: areaWidth } = useElementWidth<HTMLDivElement>();

  const toggleSheetTaskCollapsed = (taskId: string) =>
    setCollapsedSheetTaskIds((previous) =>
      previous.includes(taskId)
        ? previous.filter((id) => id !== taskId)
        : [...previous, taskId]
    );

  return (
    // The outer area is measured, uncapped, so the new layout can lay itself
    // out for the room it really has (resizing a side panel updates it
    // live). The inner one is capped on wide screens so the grid's values
    // stay near their names and the toolbar's right edge lines up with it:
    // the new layout's own cap, or 90 rem for the original layout (OP-2556).
    <div ref={areaRef}>
      <div
        className={
          preferences.sheetLayout ? sheetMaxWidthClass : 'max-w-[90rem]'
        }
      >
        <InputTasksHeader
          activeStepCategoryKey={activeStepCategoryKey}
          onStepCategoryChange={setActiveStepCategoryKey}
          activeTaskCategory={activeTaskCategory}
          onTaskCategoryChange={setActiveTaskCategory}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          viewPreferences={preferences}
          onViewPreferencesChange={updatePreferences}
          onResetViewOptions={resetViewOptions}
        />
        <TasksTableGrid
          selectedInput={selectedInput}
          draftTasks={draftTasks}
          draftZone={draftZone}
          gUnits={gUnits}
          productionCropContext={productionCropContext}
          userProfile={userProfile}
          activeStepCategoryKey={activeStepCategoryKey}
          activeTaskCategory={activeTaskCategory}
          viewPreferences={preferences}
          // The new layout's organisation default water rate, on the quick-add
          // row's spare space so nothing shifts down (OP-2556).
          onSheetMaxWidthClassChange={setSheetMaxWidthClass}
          onShowProductionFilter={() =>
            setActiveStepCategoryKey(SUMMARY_ENTITY_PRODUCTION)
          }
          quickAddTrailing={
            preferences.sheetLayout ? <OrgDefaultWaterRateField /> : undefined
          }
          sheetAvailableWidth={areaWidth}
          collapsedSheetTaskIds={collapsedSheetTaskIds}
          onToggleSheetTaskCollapsed={toggleSheetTaskCollapsed}
          onTaskColumnWidthChange={(taskColumnWidth) =>
            updatePreferences((previous) => ({ ...previous, taskColumnWidth }))
          }
          expandedTaskKeys={expandedTaskKeys}
          setExpandedTaskKeys={setExpandedTaskKeys}
          addDraftTask={addDraftTask}
          updateDraftTask={updateDraftTask}
          removeDraftTask={removeDraftTask}
          schedulePendingTaskSave={schedulePendingTaskSave}
          schedulePendingNewTaskSave={schedulePendingNewTaskSave}
          schedulePendingTaskDelete={schedulePendingTaskDelete}
        />
      </div>
    </div>
  );
};

export default InputTasks;
